import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import platformNotificationService from "../services/platformNotification.service";
import {
  PLATFORM_NOTIFICATIONS_REFRESH_EVENT,
  labelForModule,
} from "../constants/platformNotification";
import {
  formatNotificationDateTime,
  resolvePlatformNotificationPath,
} from "../utils/platformNotificationRoutes";
import "../assets/styles/platform_notifications.scss";

export default function PlatformNotificationBell({
  buttonClassName = "btn btn-link position-relative border-0",
  variant = "default",
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await platformNotificationService.unreadCount();
      const count = res?.data?.count ?? res?.count ?? 0;
      setUnreadCount(Number(count) || 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await platformNotificationService.listRecent(10);
      const list = res?.data?.items ?? res?.items ?? [];
      setItems(Array.isArray(list) ? list : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadUnreadCount(), loadRecent()]);
  }, [loadUnreadCount, loadRecent]);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => {
      void loadUnreadCount();
    }, 60000);
    const onRefresh = () => {
      void refresh();
    };
    window.addEventListener(PLATFORM_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener(PLATFORM_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    };
  }, [refresh, loadUnreadCount]);

  const updateDropdownPosition = useCallback(() => {
    const btn = wrapRef.current?.querySelector("button");
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 6;
    const viewportPadding = 8;
    const maxHeight = Math.min(420, window.innerHeight - rect.bottom - gap - viewportPadding);
    setDropdownStyle({
      position: "fixed",
      top: `${rect.bottom + gap}px`,
      right: `${Math.max(viewportPadding, window.innerWidth - rect.right)}px`,
      left: "auto",
      bottom: "auto",
      maxHeight: `${Math.max(200, maxHeight)}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownStyle(null);
      return undefined;
    }
    updateDropdownPosition();
    const onLayout = () => updateDropdownPosition();
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return undefined;
    void loadRecent();
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, loadRecent]);

  const openNotification = async (n) => {
    const id = n.id ?? n.notificationId;
    let path = resolvePlatformNotificationPath(n);

    try {
      if (id) {
        const res = await platformNotificationService.open(id);
        path = res?.data?.redirectUrl ?? res?.redirectUrl ?? path;
      }
    } catch {
      if (id && n.status !== "read") {
        try {
          await platformNotificationService.markRead(id);
        } catch {
          /* ignore */
        }
      }
    }

    setOpen(false);
    void loadUnreadCount();
    if (path) navigate(path);
    else navigate("/dashboard/notifications");
  };

  const badgeCount = unreadCount > 0 ? (unreadCount > 99 ? "99+" : unreadCount) : null;
  const bellClass =
    variant === "light"
      ? `${buttonClassName} platform-notification-bell platform-notification-bell--light`
      : `${buttonClassName} platform-notification-bell`;

  return (
    <div className="position-relative platform-notification-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={bellClass}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Notifications"
        title="Notifications"
      >
        <FaBell aria-hidden />
        {badgeCount ? (
          <span className="platform-notification-bell__badge">{badgeCount}</span>
        ) : null}
      </button>

      {open && dropdownStyle
        ? createPortal(
            <div
              ref={dropdownRef}
              className="platform-notification-dropdown dropdown-menu show shadow"
              style={dropdownStyle}
            >
              <div className="platform-notification-dropdown__header">
                <strong className="small">Notifications</strong>
                <Link to="/dashboard/notifications" className="small" onClick={() => setOpen(false)}>
                  View all
                </Link>
              </div>

              {loading ? (
                <div className="p-3 text-muted small">Loading…</div>
              ) : items.length === 0 ? (
                <div className="p-3 text-muted small">No notifications yet.</div>
              ) : (
                <>
                  <div className="platform-notification-dropdown__list">
                    {items.map((n) => {
                      const isUnread = n.status !== "read";
                      return (
                        <button
                          key={n.id}
                          type="button"
                          className={`dropdown-item text-wrap py-2 platform-notification-dropdown__item${
                            isUnread ? " platform-notification-dropdown__item--unread" : ""
                          }`}
                          onClick={() => openNotification(n)}
                        >
                          <div className="platform-notification-dropdown__title">{n.title}</div>
                          <div className="platform-notification-dropdown__message">{n.message}</div>
                          <div className="platform-notification-dropdown__meta">
                            <span>{labelForModule(n.module_name)}</span>
                            <span>{formatNotificationDateTime(n.created_at)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {unreadCount > 10 || items.length >= 10 ? (
                    <div className="platform-notification-dropdown__footer">
                      <Link
                        to="/dashboard/notifications"
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={() => setOpen(false)}
                      >
                        View all notifications
                      </Link>
                    </div>
                  ) : null}
                </>
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
